import {
  CompetencyMapOptionsQuery,
  Trial_Status,
  TrialHeaderQueryHookResult,
  TrialInfoQuery,
} from "@generated/graphql";
import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import SaveEditButtons from "components/buttons/SaveEditButtons";
import CircularLoading from "components/CircularLoading";
import Form from "components/form/Form";
import { TRIAL_STATUS_TITLE } from "constants/global";
import useIsCreate from "hooks/useIsCreate";
import ResultAssessmentModeControlProps from "pages/Testing/view/components/ResultAssessmentModeControl";
import qs from "qs";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import InfoBlock from "../../../components/InfoBlock";
import InfoTabLeft from "./components/Left";
import InfoTabRight from "./components/Right";
import TrialHistory from "./components/TrialHistory";
import useInitTrialForm from "./hooks/useInitTrialForm";
import useUpdateTesting from "./hooks/useUpdateTesting";
import { InfoInputs } from "./types";

interface InfoTabContentProps {
  trial: TrialInfoQuery["trial"];
  trialInfoLoading: boolean;
  competencyMapsOptions: CompetencyMapOptionsQuery["competencyMaps"]["data"] | undefined;
  competencyMapsLoading: boolean;
  refetchTrialHeader: TrialHeaderQueryHookResult["refetch"];
}

const InfoTabContent = ({
  trial,
  trialInfoLoading,
  competencyMapsOptions,
  competencyMapsLoading,
  refetchTrialHeader,
}: InfoTabContentProps) => {
  const isCreate = useIsCreate();
  const [isEditing, setIsEditing] = useState<boolean>(isCreate);

  const [searchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]);
  const { competencyMapId } = params as unknown as { competencyMapId?: string };
  const competencyMap =
    isCreate && competencyMapId
      ? competencyMapsOptions?.find((map) => map.id === competencyMapId)
      : trial?.examination.competencyMap && {
          ...trial?.examination.competencyMap,
          id: trial?.examination.competencyMap.originalCompetencyMapId || "",
        };

  const initFormData = useInitTrialForm(trial, competencyMap);

  const form = useForm<InfoInputs>({ values: initFormData });
  const { handleSubmit } = form;

  const { updateLoading, handleUpdateTrial } = useUpdateTesting({
    setIsEditing,
    refetchTrialHeader,
  });

  const isEditingButtonDisable = trial?.status !== Trial_Status.Created;

  if (trialInfoLoading || competencyMapsLoading) return <CircularLoading />;

  return (
    <Box sx={{ py: 2 }}>
      <Form form={form}>
        <Grid
          container
          rowSpacing={2.5}>
          <InfoBlock
            item
            LeftElement={
              <InfoTabLeft
                isEditing={isEditing}
                form={form}
              />
            }
            RightElement={
              <InfoTabRight
                competencyMaps={competencyMapsOptions}
                competencyMapsLoading={competencyMapsLoading}
                isEditing={isEditing}
                form={form}
              />
            }
            BottomElement={<ResultAssessmentModeControlProps isEditing={isEditing} />}
            ControlsElement={
              <SaveEditButtons
                isEdited={isEditing}
                onSubmit={handleSubmit(handleUpdateTrial)}
                setIsEdited={setIsEditing}
                editingButtonTooltipText={
                  isEditingButtonDisable && trial
                    ? `Редактирование возможно в статусе "${TRIAL_STATUS_TITLE[trial.status]}"`
                    : undefined
                }
                editingButtonDisable={isEditingButtonDisable}
                loading={updateLoading}
              />
            }
          />
          <Grid
            item
            xs={12}>
            <TrialHistory trial={trial} />
          </Grid>
        </Grid>
      </Form>
    </Box>
  );
};

export default InfoTabContent;
